import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown, Info, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface VirtualizedMultiSelectOption {
  value: string;
  label: string;
}

interface Props<T extends VirtualizedMultiSelectOption> {
  options: T[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Optional tooltip content per option — renders an Info icon on the row. */
  renderItemTooltip?: (option: T) => ReactNode;
}

const ITEM_HEIGHT = 36;
const LIST_HEIGHT = 320;

/**
 * Multi-select picker with the same "chips-above + trigger-below" UX as
 * `MultiSelectCombobox`, but uses `@tanstack/react-virtual` for the option
 * list — suitable for large datasets (GICS sub-industries, ~1,500 entries).
 * Mirrors tooltip rendering from `VirtualizedCombobox`.
 */
export function VirtualizedMultiSelectField<
  T extends VirtualizedMultiSelectOption
>({
  options,
  selected,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  renderItemTooltip,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedOptions = useMemo(
    () =>
      selected
        .map((v) => options.find((o) => o.value === v))
        .filter((o): o is T => !!o),
    [selected, options]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(lower));
  }, [options, search]);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
  });

  const scrollCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (node) virtualizer.measure();
    },
    [virtualizer]
  );

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    setSearch("");
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    virtualizer.measure();
  }, [search, virtualizer]);

  const toggle = (v: string) => {
    onChange(
      selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium"
            >
              {opt.label}
              <button
                type="button"
                onClick={() => toggle(opt.value)}
                className="rounded-sm hover:bg-foreground/10"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between font-normal"
          >
            <span
              className={cn(
                selectedOptions.length === 0 && "text-muted-foreground"
              )}
            >
              {selectedOptions.length > 0
                ? `${selectedOptions.length} selected`
                : placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
          align="start"
        >
          {/* Search */}
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 border-0 p-0 shadow-none focus-visible:ring-0"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="ml-1 rounded-sm opacity-50 hover:opacity-100"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Virtualized list */}
          <div
            ref={scrollCallbackRef}
            style={{ height: LIST_HEIGHT, overflow: "auto" }}
          >
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No results found.
              </div>
            ) : (
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const option = filtered[virtualItem.index];
                  const isSelected = selectedSet.has(option.value);
                  const tip = renderItemTooltip?.(option);
                  return (
                    <div
                      key={option.value}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: virtualItem.size,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(option.value)}
                        className={cn(
                          "flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground",
                          isSelected && "bg-accent/50"
                        )}
                      >
                        <Check
                          className={cn(
                            "h-4 w-4 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="flex-1 truncate text-left">
                          {option.label}
                        </span>
                        {tip && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                  className="ml-1 shrink-0 rounded p-0.5 hover:bg-foreground/10"
                                  aria-label={`More info about ${option.label}`}
                                >
                                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="left"
                                align="center"
                                className="max-w-xs text-xs"
                              >
                                {tip}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
